import React, { useEffect, useState } from "react";
import { Plus, ListPlus, Trash2 } from "lucide-react";
import api, { apiError } from "../lib/api";
import { PageHeader, Card, Badge, Loading, Modal, fmtMoney, fmtDate, EmptyState } from "../components/ui";

const TYPES = ["ARREAR", "BONUS", "ENCASHMENT", "RECOVERY", "OTHER"];
const T_TONE = { RECOVERY: "bad", ENCASHMENT: "info", BONUS: "ok", ARREAR: "warn", OTHER: "neutral" };

export default function PayInputs() {
  const [items, setItems] = useState(null);
  const [emps, setEmps] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ employeeId: "", type: "ARREAR", amount: "", period: new Date().toISOString().slice(0, 7), note: "" });

  const load = () => api.get("/payroll/inputs").then(({ data }) => setItems(data));
  useEffect(() => { load(); api.get("/employees?limit=200").then(({ data }) => setEmps(data.items)).catch(() => {}); }, []);

  const add = async (e) => {
    e.preventDefault();
    try { await api.post("/payroll/inputs", { ...form, amount: +form.amount }); setOpen(false); setForm({ ...form, amount: "", note: "" }); load(); }
    catch (err) { alert(apiError(err.response?.data?.detail)); }
  };
  const del = async (id) => { if (!window.confirm("Delete this pay input?")) return; try { await api.delete(`/payroll/inputs/${id}`); load(); } catch (e) { alert(apiError(e.response?.data?.detail)); } };
  if (!items) return <Loading />;

  return (
    <div>
      <PageHeader title="Pay Inputs" subtitle="Arrears, off-cycle bonuses, encashments & recoveries — picked up automatically by the payroll run of their period">
        <button data-testid="add-pay-input-btn" onClick={() => setOpen(true)} className="btn-accent"><Plus size={16} /> Add Pay Input</button>
      </PageHeader>
      {items.length === 0 ? <EmptyState icon={ListPlus} title="No pay inputs" hint="Add an arrear or bonus — it will flow into the payroll run for that period." /> : (
        <Card className="overflow-hidden"><div className="overflow-x-auto">
          <table className="w-full" data-testid="pay-inputs-table">
            <thead><tr><th className="th">Employee</th><th className="th">Type</th><th className="th">Amount</th><th className="th">Period</th><th className="th">Note</th><th className="th">Status</th><th className="th">Added</th><th className="th"></th></tr></thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {items.map((r) => (
                <tr key={r.id} data-testid={`pay-input-${r.id}`}>
                  <td className="td font-medium text-slate-800 dark:text-slate-100">{r.employeeName} <span className="text-xs text-slate-400">{r.employeeCode}</span></td>
                  <td className="td"><Badge value={r.type} tone={T_TONE[r.type]} /></td>
                  <td className="td font-mono font-semibold">{r.type === "RECOVERY" ? "−" : "+"}{fmtMoney(r.amount)}</td>
                  <td className="td font-mono">{r.period}</td>
                  <td className="td max-w-[200px] truncate text-slate-500">{r.note || "—"}</td>
                  <td className="td"><Badge value={r.status} tone={r.status === "PROCESSED" ? "ok" : "warn"} /></td>
                  <td className="td text-slate-500">{fmtDate(r.createdAt)}</td>
                  <td className="td text-right">{r.status === "PENDING" && <button data-testid={`del-input-${r.id}`} onClick={() => del(r.id)} className="rounded-md border border-red-200 p-1.5 text-red-600 dark:border-red-500/30"><Trash2 size={13} /></button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div></Card>
      )}
      <Modal open={open} onClose={() => setOpen(false)} title="Add Pay Input"
        footer={<><button onClick={() => setOpen(false)} className="btn-outline">Cancel</button><button form="pi-form" className="btn-accent">Add</button></>}>
        <form id="pi-form" onSubmit={add} className="space-y-4">
          <div><label className="label">Employee *</label>
            <select required data-testid="pi-emp" value={form.employeeId} onChange={(e) => setForm({ ...form, employeeId: e.target.value })} className="input"><option value="">— Select —</option>{emps.map((e) => <option key={e.id} value={e.id}>{e.fullName}</option>)}</select></div>
          <div className="grid grid-cols-3 gap-4">
            <div><label className="label">Type</label><select data-testid="pi-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="input">{TYPES.map((t) => <option key={t}>{t}</option>)}</select></div>
            <div><label className="label">Amount (₹) *</label><input required type="number" min="1" data-testid="pi-amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="input" /></div>
            <div><label className="label">Period *</label><input required type="month" data-testid="pi-period" value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} className="input" /></div>
          </div>
          <div><label className="label">Note</label><input data-testid="pi-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="input" /></div>
        </form>
      </Modal>
    </div>
  );
}
